import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Typography, 
  Space, 
  message, 
  Spin,
  Alert,
  Row,
  Col,
  Divider
} from 'antd';
import { CopyOutlined, EyeInvisibleOutlined, EyeTwoTone, LinkOutlined } from '@ant-design/icons';
import api from '../../../core/axios/axiosInstance';


const { Title, Text, Paragraph } = Typography;

const Api = () => {
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [visible, setVisible] = useState(false);

  const [staticApiKey, setStaticApiKey] = useState('');
  const [staticLoading, setStaticLoading] = useState(false);
  const [staticGenerating, setStaticGenerating] = useState(false);
  const [staticVisible, setStaticVisible] = useState(false);

  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSaving, setWebhookSaving] = useState(false);

  useEffect(() => {
    fetchCurrentApiKey();
    fetchStaticApiKey();
  }, []);

  const fetchCurrentApiKey = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api-key/current');
      if (response.data.status === 'success') {
        setApiKey(response.data.data.apiKey);
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
      if (error.response?.status !== 404) {
        message.error('Failed to fetch API key');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStaticApiKey = async () => {
    try {
      setStaticLoading(true);
      const [keyRes, configRes] = await Promise.all([
        api.get('/api-key/external/current'),
        api.get('/api-key/external/callback-url').catch(() => null),
      ]);
      if (keyRes.data.status === 'success') {
        setStaticApiKey(keyRes.data.data.apiKey);
      }
      if (configRes?.data?.callbackUrl) {
        setWebhookUrl(configRes.data.callbackUrl);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching static API key:', error);
      }
    } finally {
      setStaticLoading(false);
    }
  };

  const saveWebhookUrl = async () => {
    if (!webhookUrl.trim()) {
      message.error('Enter a webhook URL');
      return;
    }
    try {
      setWebhookSaving(true);
      await api.post('/api-key/external/callback-url', { callbackUrl: webhookUrl.trim() });
      message.success('Webhook URL saved');
    } catch (error) {
      message.error('Failed to save webhook URL');
    } finally {
      setWebhookSaving(false);
    }
  };

  const generateStaticApiKey = async () => {
    try {
      setStaticGenerating(true);
      const response = await api.post('/api-key/external/generate');
      if (response.data.status === 'success') {
        setStaticApiKey(response.data.data.apiKey);
        message.success('Static API key generated successfully');
      }
    } catch (error) {
      console.error('Error generating static API key:', error);
      message.error('Failed to generate static API key');
    } finally {
      setStaticGenerating(false);
    }
  };

  const copyStaticToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(staticApiKey);
      message.success('Static API key copied to clipboard');
    } catch (error) {
      message.error('Failed to copy');
    }
  };

  const generateNewApiKey = async () => {
    try {
      setGenerating(true);
      const response = await api.post('/api-key/generate');
      if (response.data.status === 'success') {
        setApiKey(response.data.data.apiKey);
        message.success('API key generated successfully');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      message.error('Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      message.success('API key copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      message.error('Failed to copy API key');
    }
  };

  const openApiDocumentation = () => {
    window.open('https://voycelldocs.readme.io/', '_blank');
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
              VOYCELL API
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
              You can use the VOYCELL API to integrate VOYCELL with your own workflows and applications. 
              You're able to create, update, and delete contacts, notes, and reminders. Do not share your API key.
            </Paragraph>
          </div>

          {/* API Key Section */}
          <Card 
            title="Your API Key" 
            style={{ marginBottom: '24px' }}
            extra={
              <Button 
                type="primary" 
                onClick={generateNewApiKey} 
                loading={generating}
              >
                Generate API Key
              </Button>
            }
          >
            <Paragraph style={{ marginBottom: '16px', color: '#666' }}>
              Include the following unique VOYCELL API key as a value for <code>api-key</code> header 
              for requests you make to the VOYCELL API.
            </Paragraph>
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                {apiKey ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input.Password
                      value={apiKey}
                      readOnly
                      visibilityToggle={{
                        visible: visible,
                        onVisibleChange: setVisible,
                      }}
                      iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '14px',
                        backgroundColor: '#f5f5f5'
                      }}
                      suffix={
                        <Button 
                          type="text" 
                          icon={<CopyOutlined />} 
                          onClick={copyToClipboard}
                          size="small"
                        />
                      }
                    />
                    <Alert
                      message="Keep your API key secure"
                      description="Your API key carries many privileges, so be sure to keep it secure! Do not share your secret API key in publicly accessible areas such as GitHub, client-side code, and so forth."
                      type="warning"
                      showIcon
                      style={{ marginTop: '16px' }}
                    />
                  </Space>
                ) : (
                  <Alert
                    message="No API key generated"
                    description="Click the 'Generate API Key' button above to create your API key."
                    type="info"
                    showIcon
                  />
                )}
              </>
            )}
          </Card>

          {/* Static API Key Section — for external CRM integrations */}
          <Card
            title="External CRM API Key"
            style={{ marginBottom: '24px' }}
            extra={
              <Button
                type="primary"
                onClick={generateStaticApiKey}
                loading={staticGenerating}
              >
                Generate Static Key
              </Button>
            }
          >
            <Paragraph style={{ marginBottom: '16px', color: '#666' }}>
              Use this key for external CRM integrations (e.g. Bank CRM). Send it as <code>x-api-key</code> header.
              Unlike the dynamic key above, this key <strong>never expires</strong> and is stored securely.
            </Paragraph>

            {staticLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                {staticApiKey ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input.Password
                      value={staticApiKey}
                      readOnly
                      visibilityToggle={{
                        visible: staticVisible,
                        onVisibleChange: setStaticVisible,
                      }}
                      iconRender={(v) => (v ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      style={{ fontFamily: 'monospace', fontSize: '14px', backgroundColor: '#f5f5f5' }}
                      suffix={
                        <Button type="text" icon={<CopyOutlined />} onClick={copyStaticToClipboard} size="small" />
                      }
                    />
                    <Alert
                      message="Share only with trusted CRM integrations"
                      description="Clicking Generate Static Key will rotate the key — all existing integrations using the old key will stop working immediately."
                      type="warning"
                      showIcon
                      style={{ marginTop: '16px' }}
                    />
                    <Divider style={{ margin: '16px 0 12px' }} />
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Webhook Callback URL</Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                      VOYCELL will POST results here when a contact replies.
                    </Text>
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        placeholder="https://your-crm.com/webhook/consent"
                        value={webhookUrl}
                        onChange={e => setWebhookUrl(e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: 13 }}
                      />
                      <Button type="primary" loading={webhookSaving} onClick={saveWebhookUrl}>
                        Save
                      </Button>
                    </Space.Compact>
                  </Space>
                ) : (
                  <Alert
                    message="No static API key generated"
                    description="Click 'Generate Static Key' to create a permanent key for external CRM integrations."
                    type="info"
                    showIcon
                  />
                )}
              </>
            )}
          </Card>

          {/* Documentation Section */}
          <Card title="API Documentation">
            <Row gutter={[24, 24]}>
              <Col xs={48} sm={16}>
                <div>
                  <Title level={4} style={{ marginBottom: '8px' }}>
                    Getting Started
                  </Title>
                  <Paragraph style={{ marginBottom: '16px' }}>
                    Learn how to authenticate requests, handle responses, and explore all available endpoints 
                    in our comprehensive API documentation.
                  </Paragraph>
                  <Text type="secondary">
                    Explore endpoints for contacts, leads, notes, integrations, calling, and more.
                  </Text>
                </div>
              </Col>
              <Col xs={28} sm={10} style={{ textAlign: 'center' }}>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<LinkOutlined />}
                  onClick={openApiDocumentation}
                  style={{ width: '100%' }}
                >
                  Open API Documentation
                </Button>
              </Col>
            </Row>
          </Card>

          
        </div>
      </div>
    </div>
  );
};

export default Api;